import type { AiChatSourceRef } from '@/api/ai-chat/types';

const ASSISTANT_MARK = '/aiSeeAssistant';

/** 空白新任务页。只有点开历史会话才变成 /task/:sessionId。 */
const NEW_TASK_SEGMENT = 'task';

/** 分享会话 id 固定前缀；URL 与详情请求里的 session_id 都可能是它。 */
export const SHARE_SESSION_PREFIX = 'share:';

/** 路由 / 网关可能留下 share%3A，还原后再判断前缀。 */
export const decodeSessionId = (sessionId: string) => {
  const trimmed = sessionId.trim();
  if (!trimmed) return '';
  try {
    return decodeURIComponent(trimmed);
  } catch {
    return trimmed;
  }
};

/** 是否为分享 token（只读打开，不能发送）。 */
export const isShareSessionId = (sessionId: string) =>
  decodeSessionId(sessionId).startsWith(SHARE_SESSION_PREFIX);

const CUSTOMER_SESSION_SOURCE_TYPE = 'customer_session';

/** 输入框上方引用卡片展示用，不进请求体。 */
export interface CustomerSessionPreview {
  sessionId: string;
  /** 客服会话标题。没有则卡片写「客服会话」，不展示 id。 */
  title?: string;
  focusMessageId?: string;
  /** 带 focus 时展示的该条回复正文。没有正文就不展示消息区。 */
  focusContent?: string;
}

export interface AssistantJumpParams {
  sessionId: string;
  title?: string;
  focusMessageId?: string;
  focusContent?: string;
}

const assistantRoot = (pathname: string) => {
  const index = pathname.indexOf(ASSISTANT_MARK);
  if (index < 0) return '';
  const end = index + ASSISTANT_MARK.length;
  const next = pathname[end];
  if (next && next !== '/') return '';
  return pathname.slice(0, end);
};

const tailSegments = (pathname: string) => {
  const root = assistantRoot(pathname);
  if (!root) return [];
  return pathname.slice(root.length).split('/').filter(Boolean);
};

const decodeSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return '';
  }
};

export const chatSessionIdFromPath = (pathname: string): string => {
  const segments = tailSegments(pathname);
  if (segments.length !== 2 || segments[0] !== NEW_TASK_SEGMENT) return '';
  return decodeSegment(segments[1]);
};

export const assistantSessionPath = (pathname: string, sessionId: string) => {
  const root = assistantRoot(pathname);
  if (!root || !sessionId) return '';
  return `${root}/${NEW_TASK_SEGMENT}/${encodeURIComponent(sessionId)}`;
};

/** 去掉 /task/:sessionId，回到空白新任务。不是这条地址时原样返回。 */
export const assistantBlankTaskPath = (pathname: string) =>
  pathname.replace(/\/task\/[^/]+$/, '/task');

/** 点击助手优化后携带会话id到ai chat页面 */
export const assistantNewTaskPath = (
  lv1Id: string,
  lv2Id: string,
  lv3Id: string
) => `/admin/${lv1Id}/${lv2Id}/${lv3Id}/aiSeeAssistant/${NEW_TASK_SEGMENT}`;

/** 引用客服会话。session_id 是客服会话 id，不是培训 agent 的 session_id。 */
export const buildCustomerSessionSourceRef = (
  customerSessionId: string,
  focusMessageId?: string
): AiChatSourceRef => {
  const extra: NonNullable<AiChatSourceRef['extra']> = {
    session_id: customerSessionId,
  };
  if (focusMessageId) extra.focus_message_id = focusMessageId;
  return { type: CUSTOMER_SESSION_SOURCE_TYPE, extra };
};

const buildPreview = (params: AssistantJumpParams): CustomerSessionPreview => {
  const preview: CustomerSessionPreview = {
    sessionId: params.sessionId,
  };
  const title = params.title?.trim();
  if (title) preview.title = title;
  if (params.focusMessageId) {
    preview.focusMessageId = params.focusMessageId;
    const content = params.focusContent?.trim();
    if (content) preview.focusContent = content;
  }
  return preview;
};

/** 管理端跳到助手空白页时的 history.state。不写输入框文案。 */
export const assistantJumpState = (params: AssistantJumpParams) => ({
  sourceRef: buildCustomerSessionSourceRef(
    params.sessionId,
    params.focusMessageId
  ),
  sourcePreview: buildPreview(params),
});

const readExtraString = (
  extra: Record<string, unknown> | undefined,
  key: string
) => {
  const value = extra?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : '';
};

/** 从 history.state 读出客服会话引用；缺 type 或 session_id 时视为无效。 */
export const sourceRefFromState = (state: unknown): AiChatSourceRef | null => {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { sourceRef?: unknown }).sourceRef;
  if (!raw || typeof raw !== 'object') return null;
  const { type } = raw as { type?: unknown };
  if (typeof type !== 'string' || !type.trim()) return null;
  const extraRaw = (raw as { extra?: unknown }).extra;
  const extra =
    extraRaw && typeof extraRaw === 'object'
      ? (extraRaw as Record<string, unknown>)
      : undefined;
  const sessionId = readExtraString(extra, 'session_id');
  if (!sessionId) return null;
  const focusMessageId = readExtraString(extra, 'focus_message_id');
  return buildCustomerSessionSourceRef(sessionId, focusMessageId || undefined);
};

/**
 * 从一轮请求的 source_ref 还原引用卡片。
 * 只认 customer_session，且 extra.session_id 必有。
 * 标题和消息正文不在协议里，有则带上，没有就用会话 id。
 */
export const customerPreviewFromSourceRef = (
  sourceRef: unknown
): CustomerSessionPreview | null => {
  if (!sourceRef || typeof sourceRef !== 'object') return null;
  const { type } = sourceRef as { type?: unknown };
  if (type !== CUSTOMER_SESSION_SOURCE_TYPE) return null;
  const extraRaw = (sourceRef as { extra?: unknown }).extra;
  const extra =
    extraRaw && typeof extraRaw === 'object'
      ? (extraRaw as Record<string, unknown>)
      : undefined;
  const sessionId = readExtraString(extra, 'session_id');
  if (!sessionId) return null;
  const focusMessageId = readExtraString(extra, 'focus_message_id');
  return buildPreview({
    sessionId,
    title: readExtraString(extra, 'title') || undefined,
    focusMessageId: focusMessageId || undefined,
    focusContent: readExtraString(extra, 'focus_content') || undefined,
  });
};

/** 从 history.state 读出引用卡片展示数据。 */
export const sourcePreviewFromState = (
  state: unknown
): CustomerSessionPreview | null => {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { sourcePreview?: unknown }).sourcePreview;
  if (!raw || typeof raw !== 'object') return null;
  const { sessionId } = raw as { sessionId?: unknown };
  if (typeof sessionId !== 'string' || !sessionId.trim()) return null;
  const titleRaw = (raw as { title?: unknown }).title;
  const focusIdRaw = (raw as { focusMessageId?: unknown }).focusMessageId;
  const focusContentRaw = (raw as { focusContent?: unknown }).focusContent;
  return buildPreview({
    sessionId: sessionId.trim(),
    title: typeof titleRaw === 'string' ? titleRaw : undefined,
    focusMessageId:
      typeof focusIdRaw === 'string' && focusIdRaw.trim()
        ? focusIdRaw.trim()
        : undefined,
    focusContent:
      typeof focusContentRaw === 'string' ? focusContentRaw : undefined,
  });
};
